import { Injectable } from '@angular/core';
import { ItemDataService } from '../item-data.service';
import { FlatItemData, ItemData, JewelleryTierType, TieredJewellery } from '../../models/item-data-models';
import { trackError } from '../../app.config';
import { Filter } from '../../models/filter-models';

export const JEWELLERY_ITEM_CLASSES = ['Amulets', 'Rings', 'Belts'] as const;

export function isJewelleryItemClass(itemClass: string): boolean {
  return (JEWELLERY_ITEM_CLASSES as readonly string[]).includes(itemClass);
}

@Injectable({
  providedIn: 'root'
})
export class JewelleryTierService {
  private allItemData: ItemData[];
  private flatItemData: FlatItemData[];
  private jewelleryTierData: Record<string, JewelleryTierType>;

  private defaultTieredJewellery!: TieredJewellery[];
  private tieredJewellery: TieredJewellery[] = [];

  constructor(private itemDataService: ItemDataService) {
    this.allItemData = this.itemDataService.getItemData();
    this.flatItemData = this.itemDataService.getFlatItemData();
    this.jewelleryTierData = {};

    const structuredJewelleryTierData = this.itemDataService.getJewelleryTierListData();
    // Lower tiers first so Excellent/Good override Special when a base appears in multiple lists
    const tierApplyOrder: Array<'Special' | 'Good' | 'Excellent'> = ['Special', 'Good', 'Excellent'];
    for (const tierName of tierApplyOrder) {
      const entry = structuredJewelleryTierData.find(t => t.tier === tierName);
      entry?.baseTypes.forEach(bt => {
        this.jewelleryTierData[bt] = tierName;
      });
    }

    this.buildDefaultJewelleryTierListData();
  }

  buildDefaultJewelleryTierListData() {
    const excellentItems: string[] = [];
    const goodItems: string[] = [];
    const specialItems: string[] = [];
    const otherItems: string[] = [];

    const jewelleryTierListData: { tier: JewelleryTierType, items: string[] }[] = [
      { tier: 'Excellent', items: excellentItems },
      { tier: 'Good', items: goodItems },
      { tier: 'Special', items: specialItems },
      { tier: 'Other', items: otherItems },
    ];

    this.allItemData
      .filter(i => JEWELLERY_ITEM_CLASSES.includes(i.itemClass as typeof JEWELLERY_ITEM_CLASSES[number]))
      .forEach(itemType => {
        for (const item of itemType.baseTypes) {
          const tier = this.jewelleryTierData[item.name];
          if (tier === 'Excellent') {
            excellentItems.push(item.name);
          } else if (tier === 'Good') {
            goodItems.push(item.name);
          } else if (tier === 'Special') {
            specialItems.push(item.name);
          } else {
            otherItems.push(item.name);
          }
        }
      });

    const data: TieredJewellery[] = [];

    for (const jewelleryTier of jewelleryTierListData) {
      const tier = jewelleryTier.tier;
      const items = jewelleryTier.items;

      data.push(
        ...items.map(item => ({
          name: item,
          itemClass: this.itemDataService.getFlatItemDataByBaseTypeName(item)?.itemClass as TieredJewellery['itemClass'] || 'Amulets',
          tier,
        }))
      );
    }

    this.defaultTieredJewellery = data;
  }

  combineJewelleryTierListData(filter: Filter) {
    const clone = structuredClone(this.defaultTieredJewellery) as TieredJewellery[];

    filter.customJewelleryTiers.forEach(customItem => {
      const existingItem = clone.find(item => item.name === customItem.baseType);
      if (existingItem) {
        existingItem.originalTier = existingItem.tier;
        existingItem.tier = customItem.tier;
      } else {
        trackError(new Error('Unexpected custom jewellery: ' + customItem.baseType));
      }
    });

    this.tieredJewellery.length = 0;
    this.tieredJewellery.push(...clone);
  }

  getJewelleryTierData() {
    return this.tieredJewellery;
  }
}
